import React from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { meName } from '../CustomerDemo/softphone';
import {
  CONTRACT_KIND, CSTATE, OUTCOME, PHASE, REQ_STATUS, STAGE, branchById, positionById, type CState, type Candidate, type ContractKind,
  type Employee, type HrState, type Phase, type ReqStatus,
} from './hrLogic';
import { Badge } from '../Kho/khoUi';

export { PageHeader, Kpi, Panel } from '../CskhHub/shared';
export { Badge, Chip, exportXlsx, fmtDateTime } from '../Kho/khoUi';

export const empUrl = (id: string, tab?: string) => `/personnel-management/detail?id=${id}${tab ? `&tab=${tab}` : ''}`;
export const reqUrl = (id: string) => `/job/detail?id=${id}`;
export const candUrl = (id: string) => `/candidates-view?id=${id}`;

/** Quyền nhân sự (module personnel) và tuyển dụng (module recruitment) */
export function useHrPerms() {
  const { isSuperAdmin, hasPermission } = useAuthStore();
  const can = (k: string) => isSuperAdmin || hasPermission(k);
  return {
    canCreate: can('personnel.create'), canEdit: can('personnel.edit'), canExport: can('personnel.export'),
    /** Xem lương, thuế, tài khoản ngân hàng */
    canSeePay: can('personnel.edit') || can('salary.view'),
    canRecruit: can('recruitment.create'), canRecruitEdit: can('recruitment.edit'), canApprove: can('recruitment.approve'),
    me: meName() || 'Tôi',
  };
}

export const PhaseTag = ({ phase }: { phase: Phase }) => <Badge className={PHASE[phase].badge}>{PHASE[phase].label}</Badge>;
export const KindTag = ({ kind }: { kind: ContractKind }) => <Badge className={CONTRACT_KIND[kind].badge}>{CONTRACT_KIND[kind].short}</Badge>;
export const CStateTag = ({ state }: { state: CState }) => <Badge className={CSTATE[state].badge}>{CSTATE[state].label}</Badge>;
export const ReqStatusTag = ({ status }: { status: ReqStatus }) => <Badge className={REQ_STATUS[status].badge}>{REQ_STATUS[status].label}</Badge>;

export function StageTag({ c }: { c: Candidate }) {
  if (c.outcome !== 'open') return <Badge className={OUTCOME[c.outcome].badge} title={c.closeReason}>{OUTCOME[c.outcome].label}{c.outcome !== 'hired' ? ` · ${STAGE[c.stage].short}` : ''}</Badge>;
  return (
    <Badge className="bg-white text-gray-700 border-gray-200">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: STAGE[c.stage].color }} />{STAGE[c.stage].label}
    </Badge>
  );
}

const AVATAR = ['bg-sky-500', 'bg-pink-500', 'bg-violet-500', 'bg-amber-500', 'bg-emerald-500', 'bg-indigo-500', 'bg-rose-500', 'bg-teal-500'];
export const avatarColor = (name: string) => AVATAR[Array.from(name).reduce((a, ch) => a + ch.charCodeAt(0), 0) % AVATAR.length];
export const initials = (name: string) => name.trim().split(/\s+/).slice(-1)[0]?.charAt(0).toUpperCase() || '?';

export function Avatar({ name, size = 8 }: { name: string; size?: 8 | 10 | 12 }) {
  const cls = size === 12 ? 'w-12 h-12 text-lg' : size === 10 ? 'w-10 h-10 text-base' : 'w-8 h-8 text-sm';
  return <div className={`${cls} ${avatarColor(name)} rounded-full text-white flex items-center justify-center font-bold shrink-0`}>{initials(name)}</div>;
}

/** Ô nhân sự: avatar + tên (bấm mở hồ sơ) + vị trí · chi nhánh */
export function EmpCell({ s, e, sub }: { s: HrState; e: Employee | undefined; sub?: React.ReactNode }) {
  const navigate = useNavigate();
  if (!e) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <Avatar name={e.name} />
      <div className="min-w-0">
        <button type="button" onClick={ev => { ev.stopPropagation(); navigate(empUrl(e.id)); }}
          className="font-medium text-gray-900 dark:text-gray-100 hover:text-[#3e91ab] text-left truncate block max-w-[220px]">
          {e.name}
        </button>
        <div className="text-[11px] text-gray-400 truncate max-w-[240px]">{sub ?? <>{e.code} · {positionById(s, e.positionId)?.name} · {branchById(s, e.branchId)?.short}</>}</div>
      </div>
    </div>
  );
}

const LEVEL_DOT = { crit: 'bg-rose-500', warn: 'bg-amber-500', info: 'bg-sky-400' };
export const LevelDot = ({ level }: { level: 'crit' | 'warn' | 'info' }) => <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${LEVEL_DOT[level]}`} />;

/** Ô ghi chú lỗi / cảnh báo trong form */
export function Notes({ errors = [], warnings = [] }: { errors?: string[]; warnings?: string[] }) {
  if (!errors.length && !warnings.length) return null;
  return (
    <div className="space-y-1.5 text-xs">
      {errors.map(t => <div key={t} className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-3 py-1.5">⛔ {t}</div>)}
      {warnings.map(t => <div key={t} className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-1.5">⚠ {t}</div>)}
    </div>
  );
}

export const Field = ({ label, children, hint, className = '' }: { label: string; children: React.ReactNode; hint?: React.ReactNode; className?: string }) => (
  <div className={`block ${className}`}>
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    {children}
    {hint && <div className="text-[11px] text-gray-400 mt-1">{hint}</div>}
  </div>
);
